import {
  map, filter, sortBy, uniq,
  concat, flatten,
} from 'lodash';
import { createSelector } from '@reduxjs/toolkit';
import { CategoryTypes, ProfileTypes } from '@sda/enums';
import { selectItemById, selectItemByArray } from '../lib';
import { categorySelectors } from '..';
import type { GenericList } from '@sda/helpers/helpers.types';
import type { Badge, Category, Country } from '@sda/interfaces/category';
import type { FetchDataType } from '@sda/sdk/interfaces';

const LANG = 'en';

const DEFAULT_CATEGORY_RESPONSE = { metadata: { }, lang: { }, id: null };

type CategoryResult<T> = T & {
  lang: {
    label: string;
  };
};

export const getCategories = <T extends Category & FetchDataType>(
  categories: T[] | GenericList<T>, categoryType: CategoryTypes,
): CategoryResult<T>[] => {
  const items = filter(categories, (category: T) => category?.categoryType === categoryType && category.visible === true) as T[];

  return map(items, (category) => ({ ...category, lang: category.lang && category.lang[LANG] }));
};

export const getCategoryById = createSelector(
  categorySelectors.selectById,
  (category) => category && ({
    ...category,
    lang: category.lang && category.lang[LANG],
  }),
);

/**
 * Retrieves the ActTypes category
 * @param  {any} categories
 * @return {any}
 */
export const getActTypes = createSelector(
  categorySelectors.selectAll,
  (items) => getCategories(items, CategoryTypes.ACT_TYPES),
);

export const getActTypeByName = createSelector(
  getActTypes,
  selectItemById,
  (items, actType: string) => items.find((obj) => obj.name === actType) || DEFAULT_CATEGORY_RESPONSE,
);

/**
 * Given an array of actType IDs, return a comma-separated list of actType labels
 * @param  {any} state
 * @param  {string[]} actTypes
 * @return {string}
 */
export const getActTypeLabels = createSelector(
  getActTypes,
  selectItemByArray,
  (items, selectedActTypes) => map(selectedActTypes, (actType) => {
    const category = items.find((obj) => obj.id === actType);

    if (category) {
      return category.lang?.label;
    }

    return null;
  }).filter((item) => item).join(', '),
);

export const getBadges = createSelector(
  categorySelectors.selectAll,
  (items: (Badge & FetchDataType)[]) => {
    const categories = getCategories(items, CategoryTypes.BADGES);

    return categories;
  },
);

export const getActBadges = createSelector(
  getBadges,
  (items) => filter(items, (item) => item.metadata.profileType === ProfileTypes.ACT),
);

export const getVenueBadges = createSelector(
  getBadges,
  (items) => filter(items, (item) => item.metadata.profileType === ProfileTypes.VENUE),
);

export const getBadgeByName = createSelector(
  getBadges,
  selectItemById,
  (items, name) => items.find((item) => item.name === name),
);

/**
 * Retrieves the Countries category
 * @param  {any} categories
 * @return {any}
 */
export const getCountries = createSelector(
  categorySelectors.selectAll,
  (items: (Country & FetchDataType)[]) => {
    const categories = getCategories(items, CategoryTypes.COUNTRIES);

    return categories;
  },
);

export const getCountryByIso = createSelector(
  getCountries,
  selectItemById,
  (items, iso2) => (iso2 && items.find((obj) => obj.metadata.iso2.toUpperCase() === iso2.toUpperCase())) || DEFAULT_CATEGORY_RESPONSE,
);

/**
 * Retrieves the Disabilities category
 * @param  {any} categories
 * @return {any}
 */
export const getDisabilities = createSelector(
  categorySelectors.selectAll,
  (items) => getCategories(items, CategoryTypes.DISABILITIES),
);

/**
 * Retrieves the Genders category
 * @param  {any} categories
 * @return {any}
 */
export const getGenders = createSelector(
  categorySelectors.selectAll,
  (items) => getCategories(items, CategoryTypes.GENDERS),
);

/**
 * Retrieves the Genres category
 * @param  {any} categories
 * @return {any}
 */
export const getGenres = createSelector(
  categorySelectors.selectAll,
  (items) => getCategories(items, CategoryTypes.GENRES),
);

/**
 * Given an array of genre IDs, return a comma-separated list of genre labels
 * @param  {any} state
 * @param  {string[]} genres
 * @return {string}
 */
export const getGenreLabels = createSelector(
  getGenres,
  selectItemByArray,
  (items, selectedGenres) => map(selectedGenres, (genre) => {
    if (!genre) {
      return null;
    }

    const category = items.find((obj) => obj.id === genre);

    if (category) {
      return category.lang?.label;
    }

    return null;
  }).filter((item) => item).join(', '),
);

/**
 * Retrieves the Identities category
 * @param  {any} categories
 * @return {any}
 */
export const getIdentities = createSelector(
  categorySelectors.selectAll,
  (items) => getCategories(items, CategoryTypes.IDENTITIES),
);

/**
 * Retrieves the MusicTypes category
 * @param  {any} categories
 * @return {any}
 */
export const getMusicTypes = createSelector(
  categorySelectors.selectAll,
  (items) => getCategories(items, CategoryTypes.MUSIC_TYPES),
);

/**
 * Retrieves the Sexualities category
 * @param  {any} categories
 * @return {any}
 */
export const getSexualities = createSelector(
  categorySelectors.selectAll,
  (items) => getCategories(items, CategoryTypes.SEXUALITIES),
);

/**
 * Retrieves the ShowSalesRestrictions category
 * @param  {any} categories
 * @return {any}
 */
export const getShowSalesRestrictions = createSelector(
  categorySelectors.selectAll,
  (items) => getCategories(items, CategoryTypes.SHOW_SALES_RESTRICTIONS),
);

export const getShowSalesRestrictionByName = createSelector(
  getShowSalesRestrictions,
  selectItemById,
  (items, itemName) => items.find((obj) => obj.name === itemName) || DEFAULT_CATEGORY_RESPONSE,
);

/**
 * Retrieves the VenueSizes category
 * @param  {any} categories
 * @return {any}
 */
export const getVenueSizes = createSelector(
  categorySelectors.selectAll,
  (items) => getCategories(items, CategoryTypes.VENUE_SIZES),
);

/**
 * Retrieves the VenueTypes category
 * @param  {any} categories
 * @return {any}
 */
export const getVenueTypes = createSelector(
  categorySelectors.selectAll,
  (items) => getCategories(items, CategoryTypes.VENUE_TYPES),
);

/**
 * Given a selected venue type, return its associated label
 * @param  {any} state
 * @param  {string[]} genres
 * @return {string}
 */
export const getVenueTypeLabel = createSelector(
  getVenueTypes,
  selectItemById,
  (items, selectedVenueType) => {
    if (!selectedVenueType) {
      return null;
    }

    const category = items.find((obj) => obj.id === selectedVenueType);

    if (category) {
      return category.lang?.label;
    }

    return null;
  },
);

/**
 * Retrieves a list of currencies sorted by priority and alphabetical.
 * @param  {any} categories
 * @return {Array}
 */
export const getSortedCurrencies = createSelector(
  getCountries,
  (countries) => {
    const priorityCountries = filter(countries, (country) => country.metadata.priorityCurrency);

    const nonPriorityCountries = filter(countries, (country) => !country.metadata.priorityCurrency);

    const priorityCurrencies = sortBy(
      map(
        priorityCountries,
        (country) => country.metadata.defaultCurrency,
      ),
    );

    const nonPriorityCurrencies = sortBy(
      flatten(map(
        nonPriorityCountries,
        (country) => country.metadata.supportedCurrencies,
      )),
    );

    const combinedCurrencies = uniq(concat(priorityCurrencies, nonPriorityCurrencies));

    return combinedCurrencies;
  },
);
